import { ReactElement, useState, useCallback, useEffect } from 'react'
import { Button, Text } from '../atoms'
import { useKeyboardControls } from '../../contexts/KeyboardControlsContext'
import { ControlHighlight, KeyboardControl } from '../molecules/KeyboardControls'
import { HighlightEffect } from '../../enums/KeyboardControls'

interface AnonymityTagsProps {
  onSubmit: (anonTag: string) => void | Promise<void>
}

interface TagQuestion {
  id: string
  text: string
}

const tagQuestions: TagQuestion[] = [
  { id: 'q1', text: 'What are the last 3 letters of your mother\'s name?' },
  { id: 'q2', text: 'What are the first 3 letters of your favorite type of animal?' },
  { id: 'q3', text: 'What are the first 3 letters of your least favorite vegetable?' }
]

const headerText = 'The answers you provide to the questions below are used to tie survey responses together without using any of your personally identifiable information. This is the primary method of ensuring all of your responses are anonymous.'

// Validation: exactly 3 alphabet characters (a-z, A-Z)
const isValidAnswer = (value: string): boolean => {
  return /^[a-zA-Z]{3}$/.test(value)
}

// Only allow alphabet characters, max 3
const sanitizeInput = (value: string): string => {
  return value.replace(/[^a-zA-Z]/g, '').slice(0, 3)
}

// Generate AnonTag: capitalize first letter of each answer
const generateAnonTag = (answers: Record<string, string>): string => {
  return tagQuestions.map(q => {
    const answer = answers[q.id] ?? ''
    if (answer === '') return ''
    return answer.charAt(0).toUpperCase() + answer.slice(1).toLowerCase()
  }).join('')
}

const kbControlsLayer = 'survey'
const buttonId = 'anon-tags-next-button'
const getInputId = (questionId: string): string => `anon-tag-${questionId}`

function AnonymityTags ({ onSubmit }: AnonymityTagsProps): ReactElement {
  const keyboardControls = useKeyboardControls()
  const [answers, setAnswers] = useState<Record<string, string>>({
    q1: '',
    q2: '',
    q3: ''
  })
  const [shouldFocusInput, setShouldFocusInput] = useState(true)

  const handleInputChange = useCallback((questionId: string, value: string) => {
    const sanitized = sanitizeInput(value)
    setAnswers(prev => ({ ...prev, [questionId]: sanitized }))
  }, [])

  // Auto-focus first input on mount
  useEffect(() => {
    if (!shouldFocusInput) return
    const firstInputId = getInputId(tagQuestions[0].id)
    keyboardControls.setFocusedElement(firstInputId, kbControlsLayer)
    setShouldFocusInput(false)
  }, [shouldFocusInput, keyboardControls])

  const allValid = tagQuestions.every(q => isValidAnswer(answers[q.id]))

  const handleSubmit = (): void => {
    if (!allValid) return
    const anonTag = generateAnonTag(answers)
    void onSubmit(anonTag)
  }

  return (
    <div className='flex flex-col h-full' role='form' aria-labelledby='anon-tags-heading'>
      <h2 className='sr-only' id='anon-tags-heading'>Anonymous Identification Questions</h2>
      <div className='flex-1'>
        <Text
          text={headerText}
          size='BASE'
          className='text-dopage-lightGreen'
        />

        <div className='space-y-6 mt-8'>
          {tagQuestions.map(question => {
            const inputId = getInputId(question.id)
            const hintId = `${inputId}-hint`
            const isInvalid = answers[question.id].length > 0 && !isValidAnswer(answers[question.id])
            return (
              <div key={question.id}>
                <Text
                  text={question.text}
                  size='SM'
                  className='text-dopage-lightGreen'
                />
                <span id={hintId} className='sr-only'>Enter exactly 3 letters</span>
                <div className='mt-2'>
                <ControlHighlight
                  active={keyboardControls.isEnabled && keyboardControls.isElementFocused(inputId)}
                  highlightEffect={HighlightEffect.InputOutline}
                >
                  <KeyboardControl layer={kbControlsLayer}>
                    <input
                      type='text'
                      id={inputId}
                      value={answers[question.id]}
                      onChange={(e) => handleInputChange(question.id, e.target.value)}
                      maxLength={3}
                      aria-label={question.text}
                      aria-describedby={hintId}
                      aria-required='true'
                      aria-invalid={isInvalid}
                      className='w-20 h-10 px-3 bg-dopage-black text-dopage-lightGreen border border-dopage-tealGray rounded text-center text-lg tracking-wider focus:outline-none focus:border-dopage-primary uppercase'
                    />
                  </KeyboardControl>
                </ControlHighlight>
                </div>
                {isInvalid && (
                  <p aria-live='polite' className='sr-only'>
                    Please enter exactly 3 letters
                  </p>
                )}
              </div>
            )
          })}
        </div>
      </div>

      <div className='flex justify-center mt-8'>
        <ControlHighlight
          active={keyboardControls.isEnabled && keyboardControls.isElementFocused(buttonId)}
          highlightEffect={HighlightEffect.ButtonHexOutline}
        >
          <KeyboardControl layer={kbControlsLayer}>
            <Button
              id={buttonId}
              onClick={handleSubmit}
              disabled={!allValid}
              variant='outlined'
              aria-label='Submit answers and continue to survey'
              className='px-8 py-3 font-semibold'
            >
              <span>NEXT →</span>
            </Button>
          </KeyboardControl>
        </ControlHighlight>
      </div>
    </div>
  )
}

export default AnonymityTags
export { isValidAnswer, sanitizeInput, generateAnonTag, tagQuestions }
